import SocketService, { NAMESPACES, SocketRequest } from '../SocketService';

// Singleton instance
let instance = null;

export class NotificationSocketService {
    constructor() {
        if (instance) {
            return instance;
        }

        this.socket = new SocketService(NAMESPACES.NOTIFICATIONS);
        this.connectionPromise = null;

        this.listeners = {
            onNotificationReceived: [],
            onOptimisticPending: [],
            onOptimisticConfirmed: [],
            onOptimisticFailed: [],
        };

        // Store cleanup functions
        this.cleanupFns = [];
        
        // DON'T auto-connect - wait for explicit connect call
        
        instance = this;
    }

    static getInstance() {
        if (!instance) {
            instance = new NotificationSocketService();
        }
        return instance;
    }

    setListeners(listeners) {
        this.listeners = { ...this.listeners, ...listeners };
    }

    async ensureConnected() {
        if (!this.connectionPromise) {
            this.connectionPromise = this.socket.connect()
                .then(() => {
                    this.setupEventListeners();
                })
                .catch(error => {
                    console.error('Failed to connect notification socket:', error);
                    this.connectionPromise = null;
                    throw error;
                });
        }
        return this.connectionPromise;
    }

    async connect() {
        return this.ensureConnected();
    }

    async disconnect(destroy = false) {
        try {
            // Clean up all listeners
            this.cleanupFns.forEach(cleanup => cleanup());
            this.cleanupFns = [];
            
            await this.socket.disconnect();
            this.connectionPromise = null;
            
            if (destroy) {
                instance = null;
            }
        } catch (error) {
            console.error('Error disconnecting socket:', error);
            throw error;
        }
    }

    emit(event, data) {
        return this.socket.emit(event, data);
    }

    on(event, callback) {
        const cleanup = this.socket.on(event, callback);
        this.cleanupFns.push(cleanup);
        return cleanup;
    }

    triggerListener(listener, data) {
        if (!this.listeners[listener]) {
            console.warn(`No listeners registered for: ${listener}`);
            return;
        }
        
        if (!Array.isArray(this.listeners[listener])) {
            console.error(`Listeners for ${listener} is not an array:`, this.listeners[listener]);
            return;
        }
        
        this.listeners[listener].forEach(({ callback }) => {
            try {
                callback(data);
            } catch (error) {
                console.error(`Error in ${listener} callback:`, error);
            }
        });
    }

    generateID() {
        return Math.random().toString(36).substring(2, 9);
    }

    addListener(listener, callback) {
        const id = this.generateID();
        if (!this.listeners[listener]) {
            this.listeners[listener] = [];
        }
        this.listeners[listener].push({
            id,
            callback
        });
        return id;
    }

    removeListener(listener, id) {
        if (!this.listeners[listener]) {
            console.warn(`Attempted to remove listener from non-existent event: ${listener}`);
            return;
        }
        this.listeners[listener] = this.listeners[listener].filter(({ id: listenerId }) => listenerId !== id);
    }

    setupEventListeners() {
        console.log('🔧 Setting up event listeners for NotificationSocketService...');
        
        // Don't setup if already setup
        if (this.cleanupFns.length > 0) {
            console.log('ℹ️ Event listeners already setup, skipping...');
            return;
        }

        // Set up notification event listener
        const cleanupNotification = this.on('notification', (data) => {
            console.log('🔔 Notification event received:', data?.event);
            try {
                switch (data.event) {
                    case 'notify':
                        this.triggerListener('onNotificationReceived', data);
                        break;
                    case 'optimistic:pending':
                        this.triggerListener('onOptimisticPending', data);
                        break;
                    case 'optimistic:confirmed':
                        this.triggerListener('onOptimisticConfirmed', data);
                        break;
                    case 'optimistic:failed':
                        this.triggerListener('onOptimisticFailed', data);
                        break;
                    default:
                        console.warn('⚠️ Unknown notification event:', data.event);
                }
            } catch (error) {
                console.error('❌ Error in notification event handler:', error);
            }
        });
        this.cleanupFns.push(cleanupNotification);

        // Set up disconnect listener
        const cleanupDisconnect = this.on('disconnect', (reason) => {
            console.log('🔌 NotificationSocket disconnected. Reason:', reason);
            this.connectionPromise = null;
        });
        this.cleanupFns.push(cleanupDisconnect);

        // Set up reconnect listener
        const cleanupReconnect = this.on('reconnect', (attemptNumber) => {
            console.log(`NotificationSocket reconnected after ${attemptNumber} attempts`);
        });
        this.cleanupFns.push(cleanupReconnect);
        
        console.log('✅ Event listeners setup complete for NotificationSocketService');
    }

    async notify(notification) {
        await this.ensureConnected();
        
        const request = new SocketRequest(
            this.socket.socket?.id,
            notification.id,
            'notify',
            notification
        );
        
        this.emit('notification', request.toObject());
    }

    async createOptimisticNotification({ clientEventId, notification }) {
        await this.ensureConnected();

        const request = new SocketRequest(
            this.socket.socket?.id,
            clientEventId,
            'optimistic:create',
            {
                clientEventId,
                notification,
            }
        );

        this.emit('notification', request.toObject());
    }

    async markAllAsRead(fromNotificationID) {
        await this.ensureConnected();
        
        if (!this.socket.isConnected) {
            console.warn('⚠️ Socket not connected, queueing mark as read...');
        }
        
        this.emit('notification', SocketRequest.fromObject({ 
            socketId: this.socket.socket?.id, 
            notificationId: fromNotificationID,
            event: 'read',
        }).toObject());
        
        console.log('✅ Mark all as read sent from:', fromNotificationID);
    }
}

// Export the class as default
export default NotificationSocketService;